import Link from 'next/link'
import { useRouter } from 'next/router'
import { cn } from '~/src/lib/utils'
import { useNavClickTracker } from '~/src/hooks/useNavClickTracker'

type NavLink = {
  href: string
  label: string
}

export const NAV_LINKS: NavLink[] = [
  { href: '/obsessions', label: 'Obsessions' },
  { href: '/steals', label: 'Steals' },
  { href: '/app', label: 'App' },
]

/**
 * Entry-surface attribution, so a visit from this nav is distinguishable from
 * one via the mobile sheet or an in-page tile. Follows the existing `?ref=`
 * convention (`?ref=picks`, `?ref=home-discover`) and is read on the
 * destination by `readEntryRefParam` / `classifyEntryReferrer`, which pass any
 * value straight through — no allow-list to extend.
 */
export const NAV_REF = 'header'

interface Props {
  className?: string
}

/**
 * Desktop-only primary nav that sits beside the wordmark. Opt-in per page via
 * the `showDesktopNav` prop on `LayoutFullHeader` (homepage) and `Layout` /
 * `LayoutHeader` (everywhere else), so searches, product pages and every other
 * route keep the bare header. It renders on the homepage and on the three
 * pages it links to, so the bar persists as you move between them, plus the
 * obsession collection page (`/obsessions/[slug]/[id]`), which floats the
 * same header over a hero image/video instead of the plain bar the other
 * opted-in pages use.
 *
 * Hidden below `md` because mobile already has the full `HeaderSheetMenu`
 * slide-out; showing both would duplicate the same destinations.
 */
const DesktopNav: React.FC<Props> = ({ className }) => {
  const router = useRouter()
  const trackNavClick = useNavClickTracker('desktop-nav')

  return (
    <nav
      aria-label='Primary'
      className={cn('hidden md:flex', className)}
      onClick={trackNavClick}
    >
      <ul className='flex items-center gap-9'>
        {NAV_LINKS.map(({ href, label }) => {
          // `startsWith` so nested routes keep the parent lit (e.g.
          // /steals/[brand] keeps "Steals" active). Safe for this set because
          // no entry is '/' — that would match every route.
          const isActive =
            router.pathname === href || router.pathname.startsWith(`${href}/`)

          return (
            <li key={href}>
              <Link
                // The ref param rides on the rendered URL only; `href` above
                // stays clean so the active check keeps comparing paths.
                href={`${href}?ref=${NAV_REF}`}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'relative block text-base font-medium leading-6 tracking-[-0.1px]',
                  'transition-colors duration-200 ease-motion-expo',
                  'hover:text-ink dark:hover:text-white',
                  isActive
                    ? 'text-ink dark:text-white'
                    : 'text-foreground-secondary'
                )}
              >
                {label}
                {isActive && (
                  <span
                    aria-hidden
                    className='absolute inset-x-0 top-full mt-[5px] h-0.5 rounded-[2px] bg-ink dark:bg-white'
                  />
                )}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export default DesktopNav
